/**
 * 되물음 선택지 — 후보가 여럿이라 에이전트가 **답하지 않고 물은** 자리 (DOC-20 §1.9).
 *
 * ## 왜 승낙 줄(`ConfirmRow`)이 아닌가
 *
 * 눌러도 아무것도 실행되지 않는다. 그 문장이 **새 사용자 발화로 나갈 뿐**이고, 대화는 평소처럼
 * 한 턴 더 돈다. 확인 게이트(§5.4)가 필요한 것은 쓰기이지 되묻기가 아니다 —
 * 여기에 "확인하시겠습니까" 를 붙이면 고르는 데 두 번 눌러야 한다.
 *
 * 빈 화면의 제안 칩과 같은 동작이다(`AiAgentPage` 의 `sendMessage(suggestion.example)`).
 * 실제로 같은 함수를 부른다.
 *
 * ## 이 카드가 없어도 대화는 성립한다
 *
 * 같은 후보 목록이 **답변 문장에도 들어 있다**(`agent/app/tools/_choice.py`). 카드는 사용자가
 * 문서 이름을 다시 타이핑하지 않게 할 뿐이다. 그래서 `views.ts` 규칙대로 모르는 kind 는
 * 조용히 넘겨도 안전하다.
 */

export type ChoiceOption = {
  /** 버튼에 뜨는 이름(문서명 등). */
  label: string;
  /** 누르면 그대로 전송될 발화. 원 질문을 함께 담고 있다 — 문서만 고른 채 질문을 잃지 않는다. */
  utterance: string;
  /** 한 줄 부연. 없으면 그리지 않는다 — 빈 줄이 생기면 버튼 높이가 들쭉날쭉해진다. */
  hint: string | null;
};

export type ChoicePayload = {
  /**
   * 물음 한 줄. <b>없을 수 있다</b>(2026-09-23 · DOC-94 §3 G).
   *
   * <p>슬롯 되물음(`ask` 이벤트)이 이 카드를 같이 쓰는데, 그쪽은 물음(`label`)이 <b>답변 말풍선
   * 그 자체</b>다(`agent/app/agents/issuer.py` 가 같은 문자열로 `_reply` 한다). 여기 한 번 더
   * 적으면 같은 문장이 말풍선과 카드에 나란히 선다 — 화면에 없는 말을 더하지 않는 규율에 맞게,
   * 물음이 이미 위에 있으면 버튼만 놓는다.
   */
  question?: string;
  options: ChoiceOption[];
};

export function ChoiceCard({
  payload,
  onPick,
  disabled = false,
}: {
  payload: ChoicePayload;
  onPick: (utterance: string) => void;
  disabled?: boolean;
}) {
  const { question, options } = payload;
  if (!options.length) return null;

  return (
    <div className="card p-4 space-y-3">
      {question && <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{question}</p>}

      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <button
            key={option.utterance}
            onClick={() => onPick(option.utterance)}
            disabled={disabled}
            className="min-w-0 px-3 py-2.5 rounded-lg text-left bg-white hover:bg-slate-50 disabled:opacity-50 transition-colors"
            style={{ border: '1px solid var(--border)' }}
          >
            {/* 문서명이 길어도 카드 높이는 그대로 — 줄이 늘면 격자가 들쭉날쭉해진다.
                제안 칩과 같은 규칙이다. */}
            <span className="block text-sm truncate" style={{ color: 'var(--text-primary)' }}>
              {option.label}
            </span>
            {option.hint && (
              <span className="block text-[11px] truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {option.hint}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
